import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const LOGOUT_API_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api/auth/logout`;

type AuthUser = {
  role_name?: string;
  first_name?: string;
  firstName?: string;
};

export default function UserMenu() {
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [userInitial, setUserInitial] = useState("U");
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const adminFlag = localStorage.getItem("isAdminUser");
      const userRaw = localStorage.getItem("authUser");
      const authUser = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
      const adminFromRole = String(authUser?.role_name || "").toLowerCase() === "admin";
      const firstName = String(authUser?.first_name || authUser?.firstName || "").trim();
      setIsAdmin(adminFlag === "true" || adminFromRole);
      setUserInitial(firstName ? firstName.charAt(0).toUpperCase() : "U");
    } catch {
      setIsAdmin(false);
      setUserInitial("U");
    }
  }, []);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (ref.current && targetNode && !ref.current.contains(targetNode)) {
        setOpen(false);
      }
    };

    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const clearAuthStorage = () => {
    try {
      localStorage.removeItem("authUser");
      localStorage.removeItem("isAdminUser");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userId");
      localStorage.removeItem("selectedAccountAlignedList");
    } catch {
      // Ignore storage errors.
    }
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);

    try {
      await axios.post(LOGOUT_API_URL, {}, { withCredentials: true });
    } catch {
      // Ignore logout errors and continue with client-side cleanup.
    }

    clearAuthStorage();
    setOpen(false);
    navigate("/login");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="p-2 rounded-xl border border-gray-700 bg-gray-900/70 hover:bg-gray-800 transition-colors text-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        onClick={() => setOpen((v) => !v)}
        disabled={isSigningOut}
        aria-label="User menu"
        title="User menu"
      >
        <span className="inline-flex h-5 w-5 items-center justify-center text-sm font-bold leading-none">
          {userInitial}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border rounded shadow-lg z-50">
          {!isAdmin && (
            <button
              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSigningOut}
              onClick={() => {
                setOpen(false);
                navigate("/manage-kpis");
              }}
            >
              <span className="inline-flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 6h9m-9 6h9m-9 6h9M4.5 6h.01M4.5 12h.01M4.5 18h.01"
                  />
                </svg>
                Manage KPIs
              </span>
            </button>
          )}
          {isAdmin && (
            <>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSigningOut}
                onClick={() => {
                  setOpen(false);
                  navigate("/user-management");
                }}
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-2a6 6 0 0112 0v2zm12-7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  User Management
                </span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSigningOut}
                onClick={() => {
                  setOpen(false);
                  navigate("/role-management");
                }}
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                  Role Management
                </span>
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isSigningOut}
                onClick={() => {
                  setOpen(false);
                  navigate("/account-management");
                }}
              >
                <span className="inline-flex items-center gap-2 text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 7h18M3 12h18M3 17h18"
                    />
                  </svg>
                  Account Management
                </span>
              </button>
            </>
          )}
          <button
            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 border-t disabled:cursor-not-allowed disabled:opacity-70"
            onClick={() => void handleSignOut()}
            disabled={isSigningOut}
          >
            <span className="inline-flex items-center gap-2">
              {isSigningOut ? (
                <>
                  <span
                    className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-red-400 border-t-transparent"
                    aria-hidden="true"
                  />
                  Signing out...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1"
                    />
                  </svg>
                  Sign Out
                </>
              )}
            </span>
          </button>
          {isSigningOut && (
            <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
              Ending your session securely...
            </div>
          )}
        </div>
      )}
    </div>
  );
}